import { Effect, Schema } from 'effect';
import { SiteId } from './common.js';
/**
 * Request/response DTO schemas for the client-facing payment flow.
 *
 * This is the API contract, shared by both sides of the wire:
 *  - `apps/functions` decodes requests and encodes responses against these.
 *  - `apps/app` decodes the responses it receives against the same definitions,
 *    so a server-side shape change surfaces as a decode error on the client
 *    instead of a silent `undefined`.
 *
 * Deliberately separate from the Firestore document shapes in the backend —
 * those drift (legacy fields, date encodings). Do not reuse one for both.
 *
 * Each response schema is paired with a derived type of the same name
 * (`Schema.Schema.Type<...>`), so a consumer can use `ReserveResponse` as the
 * decoder *and* as the type annotation.
 */
/** Valid payment providers for an inbound request (excludes the 'not-set' sentinel). */
export const PaymentProviderSchema = Schema.Literals([
    'vipps',
    'vippsCheckout',
    'paypal',
    'stripe',
    'free',
    'admin',
    'invoice'
]);
const MoneySchema = Schema.Struct({
    value: Schema.Number,
    currency: Schema.String
});
const UserSchema = Schema.Struct({
    uid: Schema.String,
    email: Schema.String,
    name: Schema.String,
    phone: Schema.String,
    publicName: Schema.String
});
// --- POST /payment/reserve/:site ---------------------------------------------
export const ReserveParams = Schema.Struct({ site: SiteId });
export const ReserveBody = Schema.Struct({
    // Lenient: any string is accepted and coerced; invalid -> treated as unset,
    // matching the previous toPaymentProvider behaviour.
    paymentProvider: Schema.optional(Schema.String)
});
/**
 * One struct covering both branches the handler can return: the noOp short
 * circuit and the full reserve summary. Optional fields are omitted on encode
 * when absent, so the wire shape matches the previous handler exactly.
 */
export const ReserveResponse = Schema.Struct({
    noOp: Schema.optional(Schema.Boolean),
    message: Schema.optional(Schema.String),
    orderId: Schema.optional(Schema.String),
    amount: Schema.optional(MoneySchema),
    user: Schema.optional(UserSchema),
    role: Schema.optional(Schema.String),
    paymentDescription: Schema.optional(Schema.String),
    autoBooked: Schema.optional(Schema.Boolean),
    incidentId: Schema.optional(Schema.String)
});
// --- POST /payment/session/:provider/:site/:orderId --------------------------
export const SessionParams = Schema.Struct({
    provider: PaymentProviderSchema,
    site: SiteId,
    orderId: Schema.String
});
export const SessionBody = Schema.Struct({
    callbackUrl: Schema.String
});
export const SessionResponse = Schema.Struct({
    id: Schema.String,
    raw: Schema.Any,
    autoBooked: Schema.Boolean,
    incidentId: Schema.optional(Schema.String),
    message: Schema.String
});
// --- GET /payment/check/:siteId/:orderId -------------------------------------
export const CheckParams = Schema.Struct({
    siteId: SiteId,
    orderId: Schema.String
});
export const CheckResponse = Schema.Struct({
    order_state: Schema.String,
    authorized: Schema.Boolean
});
// --- POST /payment/cancel/:site/:cart ----------------------------------------
// `cart` is legacy in the path (the client always sends 'current'); the order is
// resolved from the caller's cart, not from this segment.
export const CancelParams = Schema.Struct({
    site: SiteId,
    cart: Schema.String
});
export const CancelResponse = Schema.Struct({
    message: Schema.String,
    orderId: Schema.String,
    ticketsCancelled: Schema.Number,
    couponsCancelled: Schema.Number
});
// --- POST /payment/cancel-tickets/:site/:timeslot ----------------------------
// `timeslot` is informational; the slot is derived from the ticket keys.
export const CancelTicketsParams = Schema.Struct({
    site: SiteId,
    timeslot: Schema.String
});
export const CancelTicketsBody = Schema.Struct({
    // Note the spelling: the client sends `ticketsKeys`.
    ticketsKeys: Schema.mutable(Schema.Array(Schema.String))
});
/** Shared by cancel-tickets and release-event-ticket. */
export const ReleaseResponse = Schema.Struct({
    message: Schema.String,
    orderId: Schema.String,
    ticketsReleased: Schema.Number
});
// --- POST /payment/release-event-ticket/:site/:eventId/:subEventId -----------
export const EventTicketParams = Schema.Struct({
    site: SiteId,
    eventId: Schema.String,
    subEventId: Schema.String
});
export const ReleaseEventTicketBody = Schema.Struct({
    ticketId: Schema.String
});
// --- POST /payment/update-event-ticket/:site/:eventId/:subEventId ------------
/**
 * Tolerant partaker: existing tickets predate some of these fields, so each is
 * defaulted rather than required — decoding still yields the full EventPartaker
 * shape the program expects.
 */
const EventPartakerSchema = Schema.Struct({
    partakerId: Schema.String.pipe(Schema.withDecodingDefaultType(Effect.succeed(''))),
    handlerUid: Schema.String.pipe(Schema.withDecodingDefaultType(Effect.succeed(''))),
    handlerName: Schema.String.pipe(Schema.withDecodingDefaultType(Effect.succeed(''))),
    dogUid: Schema.String.pipe(Schema.withDecodingDefaultType(Effect.succeed(''))),
    dogName: Schema.String.pipe(Schema.withDecodingDefaultType(Effect.succeed('')))
});
/** EventFormData: free-form answers keyed by form field id. */
const EventFormDataSchema = Schema.Record(Schema.String, Schema.mutableKey(Schema.Union([Schema.String, Schema.Number, Schema.Boolean])));
export const UpdateEventTicketBody = Schema.Struct({
    ticketId: Schema.String,
    partaker: Schema.optional(EventPartakerSchema),
    extra: Schema.optional(EventFormDataSchema),
    orderExtra: Schema.optional(EventFormDataSchema)
}).pipe(Schema.check(Schema.makeFilter((body) => body.partaker || body.extra || body.orderExtra
    ? undefined
    : 'partaker, extra, or orderExtra must be provided')));
export const UpdateEventTicketResponse = Schema.Struct({
    message: Schema.String,
    ticketId: Schema.String,
    updated: Schema.Boolean
});
//# sourceMappingURL=payment.js.map